// src/pages/instructor/ManageCourse.jsx
import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { DContext } from "../../../context/Datacontext";
import CurriculumLayout from "./CurriculumLayout";

export const ManageCourse = () => {
    const { id } = useParams();
    const { BeURL ,showAlert} = useContext(DContext);

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isCourseReady, setIsCourseReady] = useState(false);

    // STATES FOR SESSIONS

    const [addingSession, setAddingSession] = useState({});

    // fetch single course
    const fetchCourse = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${BeURL}/course/${id}`, { credentials: "include" });
            const data = await res.json();
            if (data.success) {
                // use .course or fallback
                setCourse(data.course || data.courseDoc || null);
            } else {
                setCourse(null);
            }
        } catch (err) {
            console.error("Error in fecth course", err);
            setCourse(null);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (!course || !course.syllabuses) {
            setIsCourseReady(false);
            return;
        }

        // Every syllabus must have at least 1 asset
        const allSyllabusHasSession = course.syllabuses.every(
            (syllabus) => syllabus.assets && syllabus.assets.length > 0
        );
        setIsCourseReady(allSyllabusHasSession);
    }, [course]);




    useEffect(() => {
        if (id) fetchCourse();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Called by child to create a syllabus; returns created syllabus id (or null)
    const createSyllabus = async (name, order) => {
        try {
            const res = await fetch(`${BeURL}/course/${id}/syllabus`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name}),
            });
            const data = await res.json();
            if (!data.success) {
                // forward error message to child
                showAlert(data.message);
            }
            // refresh course after creation
            await fetchCourse();
            // try to return new id for auto-open; backend should return syllabus object
            return data.syllabus?.id || data.syllabus?._id || null;
        } catch (err) {
            console.log("Error in create syllbus", err);
            throw err;
        }
    };

    // ManageCourse.js (frontend)
    const addSession = async (
        syllabusId,
        sessionTitle,
        sessionDescription,
        sessionType,
        sessionDuration,
        videoFile,
        pdfFile
    ) => {
        // mark adding state
        setAddingSession((prev) => ({ ...prev, [syllabusId]: true }));

        try {
            if (!syllabusId) throw new Error("Invalid syllabusId");
            if (!sessionTitle || !sessionType) throw new Error("Title and type required");

            // Build FormData
            const formData = new FormData();
            formData.append("title", sessionTitle);
            formData.append("description", sessionDescription || "");
            formData.append("type", sessionType);
            formData.append("durationSeconds", sessionDuration ? Number(sessionDuration) : 0);

            if (videoFile) formData.append("video", videoFile);
            if (pdfFile) formData.append("pdf", pdfFile);

            // DEBUG: friendly object view
            const debugObj = {};
            formData.forEach((val, key) => {
                if (val instanceof File) debugObj[key] = { name: val.name, size: val.size, type: val.type };
                else debugObj[key] = val;
            });
         
            // Send FormData (DO NOT JSON.stringify, DO NOT set Content-Type)
            const resp = await fetch(
                `${BeURL}/course/${id}/syllabus/${syllabusId}/session`,
                {
                    method: "POST",
                    credentials: "include",
                    body: formData,
                }
            );

            // parse response
            const data = await resp.json().catch(() => {
                throw new Error(`Server returned non-json (status ${resp.status})`);
            });

            if (!resp.ok || !data?.success) {
                const msg = data?.message || `Failed to add session (status ${resp.status})`;
                throw new Error(msg);
            }

            // refresh the course list / UI
            if (typeof fetchCourse === "function") await fetchCourse();

            return data;
        } catch (err) {
            console.error("addSession err", err);
            throw err;
        } finally {
            setAddingSession((prev) => ({ ...prev, [syllabusId]: false }));
        }
    };


    const onUpdateSession = async (
        syllabusId,
        sessionId,
        title,
        description,
        type,
        duration,
        videoFile,
        pdfFile,
        imageFile
    ) => {
        try {
            const formData = new FormData();

            formData.append("title", title);
            formData.append("description", description);
            formData.append("type", type);
            formData.append("durationSeconds", String(duration));

            // IMPORTANT
            if (videoFile) {
                formData.append("video", videoFile);
            }

            if (pdfFile) {
                formData.append("pdf", pdfFile);
            }

            if (imageFile) {
                formData.append("image", imageFile);
            }

            // debug
            for (let pair of formData.entries()) {
                console.log(pair[0], pair[1]);
            }

            const response = await fetch(
                `${BeURL}/updatesession/course/${course?._id}/syllabus/${syllabusId}/session/${sessionId}`,
                {
                    method: "PUT",
                    credentials: "include",
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || "Failed to update session");
            }

            if (typeof fetchCourse === "function") {
                await fetchCourse();
            }

            return data;
        } catch (error) {
            console.error("Update Session Error:", error);
            throw error;
        }
    };


    const handleDeleteSession = async (syllabusId, sessionId) => {
        const res = await fetch(
            `${BeURL}/course/${course._id}/syllabus/${syllabusId}/session/${sessionId}`,
            {
                method: "DELETE",
                credentials: "include"
            }
        );

        const data = await res.json();

        if (!res.ok) {
            showAlert(data.message);
        }
        else{
            window.location.reload()
            showAlert(data.message)
        }
        return data; // important
    };


    const handleCourseApproval = async (courseId) => {
        try {
            const res = await fetch(
                `${BeURL}/course-approvel?courseId=${courseId}`,
                {
                    method: "GET",
                    credentials: "include"
                }
            );

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.message || "Approval request failed");
            }

            showAlert("Course submitted for admin approval");

            // refresh course
            await fetchCourse();

        } catch (err) {
            console.error("Error in course Approvel", err);
            showAlert(err.message || "Something went wrong");
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;
    if (!course) return <div className="p-6">No course found</div>;

    return (
        <div className="min-h-screen">
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
                <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 sm:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

                        {/* LEFT CONTENT */}
                        <div className="space-y-3">
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
                                {course.title}
                            </h2>

                            <p className="text-gray-500 max-w-2xl text-sm sm:text-base">
                                {course.shortDescription}
                            </p>

                            <div className="flex flex-wrap items-center gap-3 pt-2 text-sm">
                                <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full font-medium capitalize">
                                    {course?.status[course.status.length - 1]?.stage}
                                </span>

                                <span className="text-gray-500">
                                    Modules: {course?.syllabuses?.length || 0}
                                </span>
                            </div>
                        </div>

                        {/* RIGHT SIDE ACTION */}
                        <div>
                            <button
                                onClick={() => handleCourseApproval(course?._id)}
                                disabled={course?.syllabuses?.length > 0 ? false : true}
                                title={
                                    course?.syllabuses?.length > 0
                                        ? "Submit for admin approval"
                                        : !isCourseReady
                                            ? "Add at least one session in each module"
                                            : "Add at least one module"
                                }
                                className={`${course?.syllabuses?.length > 0
                                        ? "cursor-pointer"
                                        : "cursor-not-allowed"
                                    } px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-sm
                                       ${isCourseReady
                                        ? "bg-primary-600 text-white hover:bg-primary-700 hover:shadow-md"
                                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    }`}
                            >
                                Submit for Approval
                            </button>
                        </div>
                    </div>
                </div>

            
                    {/* Curriculum Layout Component */}
                    <CurriculumLayout
                        course={course}
                        onCreateSyllabus={createSyllabus}
                        onAddSession={addSession}
                        onUpdateSession={onUpdateSession}
                        addingSessionState={addingSession}
                        onDeleteSession={handleDeleteSession}
                        setCourse={setCourse}
                    />
            </div>
        </div>
    );

};

export default ManageCourse;
